import { buildQueryKey } from '@webapp/core/utils/react-query/buildQueryKey'
import { orderRepository } from '../data-access/repositories/OrderRepository'

const ONE_MINUTE = 60 * 1000

const base = ['order']

export const orderQueryConfig = {
  getOrderDetails: (orderUuid: uuid) => ({
    queryKey: buildQueryKey([...base, 'details', orderUuid]),
    queryFn: () => orderRepository.getDetails(orderUuid),
    cacheTime: ONE_MINUTE,
    staleTime: ONE_MINUTE,
  }),

  getBudgetCategoriesForOrder: ({
    orderUuid,
    orderId,
  }: {
    orderUuid: uuid
    orderId: number
  }) => ({
    queryKey: buildQueryKey([...base, orderUuid, 'budget-categories']),
    queryFn: () => orderRepository.getBudgetCategoriesForOrder(orderId),
  }),

  getSpendBySecondaryCategory: ({
    orderUuid,
    orderId,
    secondaryCategoryId,
    startDate,
    endDate,
  }: {
    orderUuid: uuid // Prefer leveraging the UUID for query key
    // The following values are required for query to run, but not always available when invalidating
    orderId?: number
    secondaryCategoryId?: number
    startDate?: ISODateString
    endDate?: ISODateString
  }) => ({
    queryKey: buildQueryKey([
      ...base,
      orderUuid,
      'spend-by-secondary-category',
      secondaryCategoryId,
      startDate,
      endDate,
    ]),
    queryFn: () => {
      if (!secondaryCategoryId || !orderId || !startDate || !endDate) {
        throw new Error(
          'Missing required parameters for getSpendBySecondaryCategory query. Ensure secondaryCategoryId, orderId, startDate, and endDate are all defined.'
        )
      }

      return orderRepository.getSpendBySecondaryCategory(
        secondaryCategoryId,
        orderId,
        startDate,
        endDate
      )
    },
  }),

  // This doesn't rely on orderUuid so technically it should be extracted out of orderQueryConfig into something like
  // budgetQueryConfig. Since it's used for legacy budgets inside the order details page, and we'll be removing
  // legacy budgets in the near-ish future, it's fine to leave it here for now rather than adding more files that will
  // be removed soon. (PUR-2613)
  getLegacyBudgetOverview: (secondaryCategoryId?: number) => ({
    queryKey: buildQueryKey([
      ...base,
      'legacy-budget-overview',
      secondaryCategoryId,
    ]),
    queryFn: () => {
      if (!secondaryCategoryId) {
        throw new Error(
          'Missing required parameters for getLegacyBudgetOverview query. Ensure that secondaryCategoryId is defined before enabling this query.'
        )
      }
      return orderRepository.getLegacyBudgetOverview(secondaryCategoryId)
    },
  }),

  // This doesn't rely on orderUuid so technically it should be extracted out of orderQueryConfig into something like
  // budgetQueryConfig. Since it's used for legacy budgets inside the order details page, and we'll be removing
  // legacy budgets in the near-ish future, it's fine to leave it here for now rather than adding more files that will
  // be removed soon. (PUR-2613)
  getLegacyBudgets: (param?: {
    accountCodeIds: number[] | undefined
    secondaryCategoryId: number | undefined
  }) => {
    const accountCodeIds = param?.accountCodeIds
    const secondaryCategoryId = param?.secondaryCategoryId

    return {
      queryKey: buildQueryKey([
        ...base,
        'legacy-budgets',
        accountCodeIds,
        secondaryCategoryId,
      ]),
      queryFn: () => {
        if (!accountCodeIds || !secondaryCategoryId) {
          throw new Error(
            'Missing required parameters for getLegacyBudgets query. Ensure that accountCodeIds and secondaryCategoryId are defined before enabling this query.'
          )
        }
        return orderRepository.getLegacyBudgets(
          accountCodeIds,
          secondaryCategoryId
        )
      },
    }
  },

  getOrderCommittingAmounts: ({
    orderUuid,
    orderId,
  }: {
    orderUuid: uuid
    orderId?: number
  }) => ({
    queryKey: buildQueryKey([...base, orderUuid, 'committing-amounts']),
    queryFn: () => {
      if (orderId === undefined) {
        throw new Error(
          'Missing required parameters for getOrderCommittingAmounts query. Ensure that orderId is defined before enabling this query.'
        )
      }

      return orderRepository.getOrderCommittingAmounts(orderId)
    },
  }),

  getApprovalWorkflowDetails: (orderUuid: uuid) => ({
    queryKey: buildQueryKey([...base, orderUuid, 'approval-workflow-details']),
    queryFn: () => orderRepository.getApprovalWorkflowDetails(orderUuid),
  }),
}
